import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { CoreModule } from './modules/core.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';

// Angular Material / CDK
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ScrollingModule } from '@angular/cdk/scrolling';

// Firebase (compat + modular)
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireDatabaseModule } from '@angular/fire/compat/database';
import { AngularFireModule } from '@angular/fire/compat';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';

// 3rd party
import { NgxStripeModule } from 'ngx-stripe';
import { Angulartics2Module } from 'angulartics2';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';

// i18n
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';

// App routing + env + consts
import { AppRoutingModule } from './routing/app-routing.module';
import { environment } from '../environments/environment';
import { FORMAT_DATE_CALENDAR } from './models/consts/formats';

// App modules
import { LibsModule } from './modules/libs.module';
import { MaterialModule } from './modules/material.module';
import { PrimeModule } from './modules/prime.module';
import { BarcodeModule } from './services/barcode/barcode.module';

// App root
import { AppComponent } from './app.component';

// Directives / Pipes
import { StopPropaClickDirective } from './directives/stop-propa-click.directive';
import { RemoveHtmlFromStringPipe } from './filters/remove-html-from-string.pipe';
import { ReplaceSpecialCharactersPipe } from './filters/replace-special-characters.pipe';

// Services
import { GlobalHttpInterceptorService } from './services/interceptor/GlobalHttpInterceptorService';

// Components - Auth
import { AuthComponent } from './components/auth/auth.component';
import { ForgotPasswordComponent } from './components/auth/forgot-password/forgot-password.component';
import { LoginComponent } from './components/auth/login/login.component';
import { RegisterComponent } from './components/auth/register/register.component';
import { RedirectFbComponent } from './components/auth/redirect-fb/redirect-fb.component';
import { ResetPasswordComponent } from './components/auth/redirect-fb/reset-password/reset-password.component';
import { ValidationMailComponent } from './components/auth/redirect-fb/validation-mail/validation-mail.component';

// Components - Order / Flow
import { FullePointofsaleComponent } from './components/order/fulle-pointofsale/fulle-pointofsale.component';
import { FulleProductComponent } from './components/order/fulle-product/fulle-product.component';
import { FulleProductV2Component } from './components/order/fulle-product-v2/fulle-product-v2.component';
import { FulleProductModeBorneComponent } from './components/order/fulle-product-mode-borne/fulle-product-mode-borne.component';
import { FulleEndorderComponent } from './components/order/fulle-endorder/fulle-endorder.component';
// import { EndorderComponent } from './components/order/endorder/endorder.component';
import { SuccessOrderComponent } from './components/order/success-order/success-order.component';
import { TableReservationComponent } from './components/order/table-reservation/table-reservation.component';
// import { OrderAtTablePageComponent } from './components/order/orderattable/orderattable.component';

import { WaitingNotesComponent } from './components/order/waiting-notes/waitingnotes.component';
import { ItemLineComponent } from './components/order/waiting-notes/component/item-line/item-line.component';
import { DialogWrapperComponent } from './components/order/waiting-notes/component/dialog-wrapper/dialog-wrapper.component';
import { StripeDialogWrapperComponent } from './components/order/waiting-notes/component/stripe-dialog-wrapper/stripe-dialog-wrapper.component';
import { FreeAmountDialogComponent } from './components/order/waiting-notes/dialogs/free-amount-dialog/free-amount-dialog.component';
import { FullyPaidDialogComponent } from './components/order/waiting-notes/dialogs/fully-paid-dialog/fully-paid-dialog.component';
import { InfoDialogComponent } from './components/order/waiting-notes/dialogs/info-dialog/info-dialog.component';
import { NoteNotFoundDialogComponent } from './components/order/waiting-notes/dialogs/note-not-found-dialog/note-not-found-dialog.component';
import { OptionsDialogComponent } from './components/order/waiting-notes/dialogs/options-dialog/options-dialog.component';
import { OverPaymentDialogComponent } from './components/order/waiting-notes/dialogs/over-payment-dialog/over-payment-dialog.component';
import { PartageDialogComponent } from './components/order/waiting-notes/dialogs/partage-dialog/partage-dialog.component';
import { PaymentHistoryDialogComponent } from './components/order/waiting-notes/dialogs/payment-history-dialog/payment-history-dialog.component';
import { PaymentMethodDialogComponent } from './components/order/waiting-notes/dialogs/payment-method-dialog/payment-method-dialog.component';
import { RecapPaymentDialogComponent } from './components/order/waiting-notes/dialogs/recap-payment-dialog/recap-payment-dialog.component';
import { SelectItemsDialogComponent } from './components/order/waiting-notes/dialogs/select-items-dialog/select-items-dialog.component';
import { ShareItemsDialogComponent } from './components/order/waiting-notes/dialogs/share-items-dialog/share-items-dialog.component';
import { TipsDialogComponent } from './components/order/waiting-notes/dialogs/tips-dialog/tips-dialog.component';

// Components - Includes (headers/footers/data/etc.)
import { HeaderAuthComponent } from './components/includes/headers/header-auth/header-auth.component';
import { HeaderBookingComponent } from './components/includes/headers/header-booking/header-booking.component';
import { HeaderClientComponent } from './components/includes/headers/header-client/header-client.component';
import { HeaderOrderComponent } from './components/includes/headers/header-order/header-order.component';
import { FooterClientComponent } from './components/includes/footers/footer-client/footer-client.component';
import { HandleSidenavComponent } from './components/includes/footers/handle-sidenav/handle-sidenav.component';
import { FulleHandleSidenavComponent } from './components/includes/footers/fulle/fulle-handle-sidenav/fulle-handle-sidenav.component';
import { PoweredFulleComponent } from './components/includes/footers/powered-fulle/powered-fulle.component';

import { LanguagesComponent } from './components/includes/languages/languages.component';
import { NeedConnectionComponent } from './components/includes/need-connection/need-connection.component';
import { NeedBillingAddressComponent } from './components/includes/need-billing-address/need-billing-address.component';
import { NeedPaymentTypeComponent } from './components/includes/need-payment-type/need-payment-type.component';

// import { PageLoaderComponent } from './components/includes/loaders/page-loader/page-loader.component';
import { FullePageLoaderComponent } from './components/includes/loaders/fulle-page-loader/fulle-page-loader.component';
import { PopupLoaderComponent } from './components/includes/loaders/popup-loader/popuploader.component';

import { DateOrderComponent } from './components/includes/data/date-order/date-order.component';
import { InfoOrderComponent } from './components/includes/data/info-order/info-order.component';
import { PanierComponent } from './components/includes/data/panier/panier.component';
import { SideDataOrderComponent } from './components/includes/data/side-data-order/side-data-order.component';
import { VersionComponent } from './components/includes/data/version/version.component';
import { FilterDateFromToComponent } from './components/includes/data/filter-date-from-to/filter-date-from-to.component';
import { FidelitySliderProductComponent } from './components/includes/data/fidelity-slider-product/fidelity-slider-product.component';
import { SliderProdGalleryComponent } from './components/includes/data/slider-prod-gallery/slider-prod-gallery.component';
import { RubProdListComponent } from './components/includes/data/rub-prod-list/rub-prod-list.component';
import { FulleRubProdListComponent } from './components/includes/data/fulle/fulle-rub-prod-list/fulle-rub-prod-list.component';
import { FindedProdsListComponent } from './components/includes/data/finded-prods-list/finded-prods-list.component';
import { FulleFindedProdsListComponent } from './components/includes/data/fulle/fulle-finded-prods-list/fulle-finded-prods-list.component';
import { CardsBackofficeClientComponent } from './components/includes/data/cards-backoffice-client/cards-backoffice-client.component';
import { CardsEndorderComponent } from './components/includes/data/cards-endorder/cards-endorder.component';
import { AddressesBackofficeClientComponent } from './components/includes/data/addresses-backoffice-client/addresses-backoffice-client.component';
import { AddressesEndorderComponent } from './components/includes/data/addresses-endorder/addresses-endorder.component';
import { AddressesAllIncComponent } from './components/includes/data/addresses-all-inc/addresses-all-inc.component';
import { CardsIncComponent } from './components/includes/data/cards-inc/cards-inc.component';
import { BasketProductComponent } from './components/includes/data/basket-product/basket-product.component';
// import { BasketEndorderComponent } from './components/includes/data/basket-endorder/basket-endorder.component';
import { FulleBasketEndorderComponent } from './components/includes/data/fulle/fulle-basket-endorder/fulle-basket-endorder.component';
import { PaymentCardEndorderComponent } from './components/includes/data/payment-card-endorder/payment-card-endorder.component';
// import { PaymentsMethodsEndorderComponent } from './components/includes/data/payments-methods-endorder/payments-methods-endorder.component';
import { FullePaymentsMethodsEndorderComponent } from './components/includes/data/fulle/fulle-payments-methods-endorder/fulle-payments-methods-endorder.component';
import { FakeRadioButtonComponent } from './components/includes/data/fake-radio-button/fake-radio-button.component';
import { CredentialsComponent } from './components/includes/credentials/credentials.component';
import { FulleMerchantTermsComponent } from './components/includes/fulle-merchant-terms/fulle-merchant-terms.component';
import { InvoiceRechargeComponent } from './components/includes/ext/invoice-recharge/invoice-recharge.component';
import { BarcodeClientComponent } from './components/includes/barcode-client/barcode-client.component';
import { OrderInfoThumbnailComponent } from './components/includes/order-info-thumbnail/order-info-thumbnail.component';
import { ProductItemListComponent } from './components/includes/product-item-list/product-item-list.component';
import { RubricItemListComponent } from './components/includes/data/rubric-item-list/rubric-item-list.component';
import { FulleThumbnailShedulesComponent } from './components/includes/fulle-thumbnail-shedules/fulle-thumbnail-shedules.component';

// Components - Client
import { MainComponent } from './components/client/main.component';
import { AccountComponent } from './components/client/account/account.component';
import { AddressesComponent } from './components/client/addresses/addresses.component';
import { CardsComponent } from './components/client/cards/cards.component';
import { OrdersComponent } from './components/client/orders/orders.component';
import { MerchantsComponent } from './components/client/merchants/merchants.component';
import { DashboardComponent } from './components/client/dashboard/dashboard.component';
import { RefillsComponent } from './components/client/refills/refills.component';
import { RechargeComponent } from './components/client/recharge/recharge.component';
import { MerchantOptionsComponent } from './components/client/merchant-options/merchant-options.component';
import { FidelityComponent } from './components/client/fidelity/fidelity.component';
import { CashlessOptionsComponent } from './components/client/cashless-options/cashless-options.component';
import { NotesComponent } from './components/client/notes/notes.component';
import { CreditsComponent } from './components/client/credits/credits.component';
import { CreditsHistoryComponent } from './components/client/credits/credits-history/credits-history.component';
import { CreditsDueComponent } from './components/client/credits/credits-due/credits-due.component';

// Components - Booking
import { ReservationComponent } from './components/client/booking/reservation/reservation.component';
import { MerchantComponent } from './components/client/booking/merchant/merchant.component';

// Components - Release / Home / Misc
import { ReleaseNoteComponent } from './components/release-note/release-note.component';
import { HomeInterfaceComponent } from './components/home-interface/home-interface.component';
import { CreateUrlTokenComponent } from './components/url-token/create-url-token.component';
import { StripeWebComponent } from './components/stripe-web/stripe-web.component';
import { CapturedPaymentComponent } from './components/captured-payment/captured-payment.component';
import { MyPosComponent } from './components/my-pos/my-pos.component';
import { MyPosEmbeddedButtonsComponent } from './components/my-pos-embedded-buttons/my-pos-embedded-buttons.component';
import { ClientAccountLogsComponent } from './components/api-logs/client-account-logs/client-account-logs.component';

// Components - Redirect / Errors
import { ErrorUrlComponent } from './components/redirect/error/error-url/error-url.component';
import { DisabledOrderComponent } from './components/redirect/error/disabled-order/disabled-order.component';

// Dialogs
import { AddressComponent } from './components/dialogs/client-address/address/address.component';
import { DeleteaddressComponent } from './components/dialogs/client-address/deleteaddress/deleteaddress.component';
import { CredentialComponent } from './components/dialogs/credential/credential.component';
import { FulleSalemethodComponent } from './components/dialogs/fulle/fulle-salemethod/fulle-salemethod.component';
import { ProdComponent } from './components/dialogs/prod/prod.component';
import { ConfirmorderComponent } from './components/dialogs/confirm-order/confirmorder.component';
import { PreventLossPanierComponent } from './components/dialogs/prevent-loss-panier/prevent-loss-panier.component';
import { PreventLossAllComponent } from './components/dialogs/prevent-loss-all/prevent-loss-all.component';
import { CheckAddressComponent } from './components/dialogs/check-address/check-address.component';
// import { TermsConditionsComponent } from './components/dialogs/terms-conditions/terms-conditions.component';
import { AlertMiniAmountComponent } from './components/dialogs/alert-mini-amount/alert-mini-amount.component';
import { AlertMaxWeightComponent } from './components/dialogs/alert-max-weight/alert-max-weight.component';
import { WeightsTableComponent } from './components/dialogs/weights-table/weights-table.component';
import { AlertInvalidCountryComponent } from './components/dialogs/alert-invalid-country/alert-invalid-country.component';
import { EligibleCitiesComponent } from './components/dialogs/eligible-cities/eligible-cities.component';
import { AlertInvalidCityComponent } from './components/dialogs/alert-invalid-city/alert-invalid-city.component';
import { AlertInvalidSlotComponent } from './components/dialogs/alert-invalid-slot/alert-invalid-slot.component';
import { AlertRecoveryOrderChangesComponent } from './components/dialogs/alert-recovery-order-changes/alert-recovery-order-changes.component';
import { AlertInvalidRubricSlotComponent } from './components/dialogs/alert-invalid-rubric-slot/alert-invalid-rubric-slot.component';
import { AlertInvalidPricePeriodComponent } from './components/dialogs/alert-invalid-price-period/alert-invalid-price-period.component';
import { PromoShedulesComponent } from './components/dialogs/promo-shedules/promo-shedules.component';
import { FulleShedulesComponent } from './components/dialogs/fulle/fulle-shedules/fulle-shedules.component';
import { BarcodeComponent } from './components/dialogs/booking/barcode/barcode.component';
import { AlertStockComponent } from './components/dialogs/alert-stock/alert-stock.component';
import { AlertAlcoolComponent } from './components/dialogs/alert-alcool/alert-alcool.component';
import { MondialRelayComponent } from './components/dialogs/mondial-relay/mondial-relay.component';

import { ConfirmRechargeComponent } from './components/dialogs/confirm-recharge/confirm-recharge.component';
import { ConfirmBlockCardComponent } from './components/dialogs/confirm-block-card/confirm-block-card.component';
import { RequiredInformationsComponent } from './components/dialogs/required-informations/required-informations.component';
import { StripeCardScaComponent } from './components/dialogs/stripe-card-sca/stripe-card-sca.component';
import { PaymentMethodsComponent } from './components/dialogs/payment-methods/payment-methods.component';
import { PayzenComponent } from './components/dialogs/payzen/payzen.component';
import { FidelityOrderComponent } from './components/dialogs/fidelity-order/fidelity-order.component';
import { ClientPhoneComponent } from './components/dialogs/client-phone/client-phone.component';
import { ClientPasswordComponent } from './components/dialogs/client-password/client-password.component';
import { ConfirmCreditsComponent } from './components/dialogs/confirm-credits/confirm-credits.component';
import { AlertArchiveSaleMethodComponent } from './components/dialogs/alert-archive-sale-method/alert-archive-sale-method.component';
import { ConfirmSavePaymentComponent } from './components/dialogs/confirm-save-payment/confirm-save-payment.component';
import { PaymentsMethodsCardComponent } from './components/dialogs/payments-methods-card/payments-methods-card.component';
import { PaymentTouchTerminalComponent } from './components/dialogs/payment-touch-terminal/payment-touch-terminal.component';
import { UserTouchTerminalComponent } from './components/dialogs/user-touch-terminal/user-touch-terminal.component';
import { ExpireDateDialogComponent } from './components/dialogs/expire-date-dialog/expire-date-dialog.component';
import { CancelOrderComponent } from './components/dialogs/cancel-order/cancel-order.component';
import { DeleteClientAccountComponent } from './components/dialogs/delete-client-account/delete-client-account.component';
import { AlertExpireDateProductDialogComponent } from './components/dialogs/alert-expire-date-product-dialog/alert-expire-date-product-dialog.component';
import { ProductSuggestionsComponent } from './components/dialogs/product-suggestions/product-suggestions.component';
import { FormulesSuggestionsComponent } from './components/dialogs/formules-suggestions/formules-suggestions.component';
import { DescriptionOrderComponent } from './components/dialogs/description-order/description-order.component';

import { ProductsTimeSlotSelectorComponent } from './components/dialogs/products-time-slot-selector/products-time-slot-selector.component';
// import { SelectProductsTimeSlotComponent } from './components/dialogs/select-products-time-slot/select-products-time-slot.component';
import { FulleSelectProductsTimeSlotComponent } from './components/dialogs/fulle/fulle-select-products-time-slot/fulle-select-products-time-slot.component';
import { ErrorDialogComponent } from './components/dialogs/fulle/fulle-error-dialog/error-dialog.component';

// MC connect table
import { PngGeneratorComponent } from './components/mc-connect-table/png-generator/png-generator-component.component';
import { DeviceListComponent } from './components/mc-connect-table/device-list/device-list.component';
import { ChangeDeviceAffectationComponent } from './components/mc-connect-table/change-device-affectation/change-device-affectation.component';
import { UpdateDeviceScreenComponent } from './components/mc-connect-table/update-device-screen/update-device-screen.component';
import { FulleSelectionSMComponent } from './components/order/fulle-selection-SM/fulle-selection-SM.component';
import { FulleSelectSlotDialogComponent } from './components/dialogs/fulle/fulle-select-slot/fulle-select-slot.component';

const customDialogConfig: MatDialogConfig = {
  panelClass: 'my-custom-dialog-container',
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    '/assets/i18n/',
    `.json?cb=${new Date().toISOString().replace(/\.|:|-/g, '')}-${environment.version}`
  );
}

@NgModule({
  declarations: [
    // Root
    AppComponent,

    // Directives / Pipes
    StopPropaClickDirective,
    RemoveHtmlFromStringPipe,
    ReplaceSpecialCharactersPipe,

    // Auth
    AuthComponent,
    ForgotPasswordComponent,
    LoginComponent,
    RegisterComponent,
    RedirectFbComponent,
    ResetPasswordComponent,
    ValidationMailComponent,

    // Client
    MainComponent,
    AccountComponent,
    AddressesComponent,
    CardsComponent,
    OrdersComponent,
    MerchantsComponent,
    DashboardComponent,
    RefillsComponent,
    RechargeComponent,
    MerchantOptionsComponent,
    FidelityComponent,
    CashlessOptionsComponent,
    NotesComponent,
    CreditsComponent,
    CreditsHistoryComponent,
    CreditsDueComponent,

    // Order
    FullePointofsaleComponent,
    FulleProductComponent,
    FulleProductV2Component,
    FulleSelectionSMComponent,
    FulleProductModeBorneComponent,
    // OrderAtTablePageComponent,
    // EndorderComponent,
    FulleEndorderComponent,
    SuccessOrderComponent,
    TableReservationComponent,
    WaitingNotesComponent,
    ItemLineComponent,
    DialogWrapperComponent,
    StripeDialogWrapperComponent,

    // Includes
    HeaderAuthComponent,
    HeaderBookingComponent,
    HeaderClientComponent,
    HeaderOrderComponent,
    FooterClientComponent,
    HandleSidenavComponent,
    FulleHandleSidenavComponent,
    PoweredFulleComponent,

    LanguagesComponent,
    NeedConnectionComponent,
    NeedBillingAddressComponent,
    NeedPaymentTypeComponent,

    FullePageLoaderComponent,
    PopupLoaderComponent,

    DateOrderComponent,
    InfoOrderComponent,
    PanierComponent,
    SideDataOrderComponent,
    VersionComponent,
    FilterDateFromToComponent,
    FidelitySliderProductComponent,
    SliderProdGalleryComponent,
    RubProdListComponent,
    FulleRubProdListComponent,
    FindedProdsListComponent,
    FulleFindedProdsListComponent,
    CardsBackofficeClientComponent,
    CardsEndorderComponent,
    AddressesBackofficeClientComponent,
    AddressesEndorderComponent,
    AddressesAllIncComponent,
    CardsIncComponent,
    BasketProductComponent,
    FulleBasketEndorderComponent,
    PaymentCardEndorderComponent,
    FullePaymentsMethodsEndorderComponent,
    FakeRadioButtonComponent,
    CredentialsComponent,
    FulleMerchantTermsComponent,
    InvoiceRechargeComponent,
    BarcodeClientComponent,
    OrderInfoThumbnailComponent,
    ProductItemListComponent,
    RubricItemListComponent,
    FulleThumbnailShedulesComponent,

    // Booking
    ReservationComponent,
    MerchantComponent,

    // Release / Misc
    ReleaseNoteComponent,
    HomeInterfaceComponent,
    CreateUrlTokenComponent,
    StripeWebComponent,
    CapturedPaymentComponent,
    MyPosComponent,
    MyPosEmbeddedButtonsComponent,
    ClientAccountLogsComponent,

    // Redirect / Errors
    ErrorUrlComponent,
    DisabledOrderComponent,

    // Dialogs
    AddressComponent,
    DeleteaddressComponent,
    CredentialComponent,
    FulleSalemethodComponent,
    ProdComponent,
    ConfirmorderComponent,
    PreventLossPanierComponent,
    PreventLossAllComponent,
    CheckAddressComponent,
    // TermsConditionsComponent,
    AlertMiniAmountComponent,
    AlertMaxWeightComponent,
    WeightsTableComponent,
    AlertInvalidCountryComponent,
    EligibleCitiesComponent,
    AlertInvalidCityComponent,
    AlertInvalidSlotComponent,
    AlertRecoveryOrderChangesComponent,
    AlertInvalidRubricSlotComponent,
    AlertInvalidPricePeriodComponent,
    PromoShedulesComponent,
    FulleShedulesComponent,
    BarcodeComponent,
    AlertStockComponent,
    AlertAlcoolComponent,
    MondialRelayComponent,

    ConfirmRechargeComponent,
    ConfirmBlockCardComponent,
    RequiredInformationsComponent,
    StripeCardScaComponent,
    PaymentMethodsComponent,
    PayzenComponent,
    FidelityOrderComponent,
    ClientPhoneComponent,
    ClientPasswordComponent,
    ConfirmCreditsComponent,
    AlertArchiveSaleMethodComponent,
    ConfirmSavePaymentComponent,
    PaymentsMethodsCardComponent,
    PaymentTouchTerminalComponent,
    UserTouchTerminalComponent,
    ExpireDateDialogComponent,
    CancelOrderComponent,
    DeleteClientAccountComponent,
    AlertExpireDateProductDialogComponent,
    ProductSuggestionsComponent,
    FormulesSuggestionsComponent,
    DescriptionOrderComponent,

    ProductsTimeSlotSelectorComponent,
    // SelectProductsTimeSlotComponent
    FulleSelectProductsTimeSlotComponent,
    ErrorDialogComponent,

    // Waiting Notes dialogs
    FreeAmountDialogComponent,
    FullyPaidDialogComponent,
    InfoDialogComponent,
    NoteNotFoundDialogComponent,
    OptionsDialogComponent,
    OverPaymentDialogComponent,
    PartageDialogComponent,
    PaymentHistoryDialogComponent,
    PaymentMethodDialogComponent,
    RecapPaymentDialogComponent,
    SelectItemsDialogComponent,
    ShareItemsDialogComponent,
    TipsDialogComponent,

    FulleSelectSlotDialogComponent,

    // MC Connect Table
    PngGeneratorComponent,
    DeviceListComponent,
    ChangeDeviceAffectationComponent,
    UpdateDeviceScreenComponent,
  ],
  imports: [
    // Angular
    CommonModule,
    BrowserModule.withServerTransition({ appId: 'clientOrder' }),
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,

    // Angular Material / CDK
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    ScrollingModule,

    // 3rd party
    Angulartics2Module,
    NgxStripeModule.forRoot(environment.stripe.stripePublicKey),
    NgxMaterialTimepickerModule,

    // Firebase
    AngularFireModule.initializeApp(environment.firebaseClient),
    AngularFireDatabaseModule,
    AngularFireAuthModule,
    provideFirebaseApp(() => initializeApp(environment.firebaseClient)),
    provideAuth(() => getAuth()),

    // App
    AppRoutingModule,
    BarcodeModule,

    // Core modules
    CoreModule,
    MaterialModule,
    LibsModule,
    PrimeModule,

    // Translate
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
  ],
  providers: [
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: customDialogConfig,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: FORMAT_DATE_CALENDAR,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GlobalHttpInterceptorService,
      multi: true,
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule { }
